import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserMinus, Download, Search, Filter, RefreshCw,
  ArrowLeft, Eye, Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WithdrawalList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [withdrawals, setWithdrawals] = useState([]);
  const [summary, setSummary] = useState({ total_count: 0, total_refunded: 0, total_deductions: 0 });
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await fetch(`${API_URL}/api/student-withdrawal/list?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setWithdrawals(data.withdrawals || []);
        setSummary(data.summary || { total_count: 0, total_refunded: 0, total_deductions: 0 });
      }
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!withdrawals.length) return;

    const headers = ['Withdrawal No', 'Date', 'Student Name', 'Student ID', 'Course', 'Reason', 'Gross Refund', 'Deductions', 'Net Refund', 'Refund Mode'];
    const rows = withdrawals.map(w => [
      w.withdrawal_no,
      w.withdrawal_date,
      w.student_name,
      w.student_roll,
      w.course_name,
      w.reason,
      w.gross_refund,
      w.total_deductions,
      w.net_refund,
      w.refund_mode
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `student_withdrawals_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Withdrawal History</h1>
            <p className="text-gray-600 mt-1">All processed student withdrawals</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchWithdrawals}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button variant="outline" onClick={exportToCSV} disabled={!withdrawals.length}>
            <Download className="h-4 w-4 mr-2" /> Export
          </Button>
          <Button onClick={() => navigate('/admission/student-withdrawal')}>
            <UserMinus className="h-4 w-4 mr-2" /> New Withdrawal
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <UserMinus className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{summary.total_count}</p>
                <p className="text-sm text-gray-500">Total Withdrawals</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Calendar className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">₹{summary.total_refunded?.toLocaleString('en-IN')}</p>
                <p className="text-sm text-gray-500">Total Refunded</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Filter className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">₹{summary.total_deductions?.toLocaleString('en-IN')}</p>
                <p className="text-sm text-gray-500">Total Deductions</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-40">
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="w-40">
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <Button onClick={fetchWithdrawals}>Apply Filter</Button>
            <Button 
              variant="outline" 
              onClick={() => {
                setFilters({ start_date: '', end_date: '' });
                fetchWithdrawals();
              }}
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Withdrawals Table */}
      <Card>
        <CardHeader>
          <CardTitle>Withdrawal Records</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
            </div>
          ) : withdrawals.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <UserMinus className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No withdrawal records found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="withdrawals-table">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Ref No.</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Date</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Student</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Course</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Reason</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">Gross</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">Deductions</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">Net Refund</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-600">Mode</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map((withdrawal) => (
                    <tr key={withdrawal.id} className="border-b hover:bg-gray-50">
                      <td className="p-3">
                        <span className="font-mono text-sm">{withdrawal.withdrawal_no}</span>
                      </td>
                      <td className="p-3 text-gray-600">{withdrawal.withdrawal_date}</td>
                      <td className="p-3">
                        <p className="font-medium">{withdrawal.student_name}</p>
                        <p className="text-sm text-gray-500">{withdrawal.student_roll}</p>
                      </td>
                      <td className="p-3 text-gray-600">{withdrawal.course_name}</td>
                      <td className="p-3 text-gray-600">{withdrawal.reason}</td>
                      <td className="p-3 text-right">₹{withdrawal.gross_refund?.toLocaleString('en-IN')}</td>
                      <td className="p-3 text-right text-red-600">
                        {withdrawal.total_deductions > 0 ? `-₹${withdrawal.total_deductions?.toLocaleString('en-IN')}` : '-'}
                      </td>
                      <td className="p-3 text-right font-bold text-green-600">
                        ₹{withdrawal.net_refund?.toLocaleString('en-IN')}
                      </td>
                      <td className="p-3 text-center">
                        <span className="px-2 py-1 bg-gray-100 rounded text-sm">
                          {withdrawal.refund_mode}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 font-bold">
                    <td colSpan="5" className="p-3 text-right">Total:</td>
                    <td className="p-3 text-right">₹{summary.total_refunded + summary.total_deductions}</td>
                    <td className="p-3 text-right text-red-600">-₹{summary.total_deductions?.toLocaleString('en-IN')}</td>
                    <td className="p-3 text-right text-green-600">₹{summary.total_refunded?.toLocaleString('en-IN')}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
